import { PgnParser } from "./PgnParser";
import { isValidSquare } from "../minigame/BoardUtils";
import { CHESS_EMPTY_BOARD_FEN } from "../constants";
/**
 * MiniGameParser - Utility for mini-game PGN handling.
 *
 * Supports DSL format in PGN headers:
 *   [MiniGame "SQUARE_HUNT:target=e4;moveLimit=10"]
 *
 * DSL Format:
 *   HANDLER:key1=value1;key2=value2
 *
 * Values are auto-converted:
 *   - "true"/"false" → boolean
 *   - numeric strings → number
 *   - everything else → string
 *
 * Note: Use PgnParser.hasMiniGameHeader() to check if a PGN is a mini-game.
 */
export class MiniGameParser {
    /**
     * Parse a MiniGame DSL header value into config data.
     *
     * Examples:
     *   "SQUARE_HUNT" → { handler: "SQUARE_HUNT", params: {} }
     *   "SQUARE_HUNT:target=e4" → { handler: "SQUARE_HUNT", params: { target: "e4" } }
     *   "SQUARE_HUNT:target=e4;moveLimit=10" → { handler: "SQUARE_HUNT", params: { target: "e4", moveLimit: 10 } }
     */
    static parseMiniGameHeader(headerValue) {
        const colonIndex = headerValue.indexOf(":");
        const handler = colonIndex === -1 ? headerValue : headerValue.slice(0, colonIndex);
        const paramString = colonIndex === -1 ? "" : headerValue.slice(colonIndex + 1);
        const params = {};
        if (paramString) {
            paramString.split(";").forEach((pair) => {
                const eqIndex = pair.indexOf("=");
                if (eqIndex === -1)
                    return;
                const key = pair.slice(0, eqIndex).trim();
                const val = pair.slice(eqIndex + 1).trim();
                if (!key)
                    return;
                // Auto-convert types
                if (val === "true") {
                    params[key] = true;
                }
                else if (val === "false") {
                    params[key] = false;
                }
                else if (val !== "" && !isNaN(Number(val))) {
                    params[key] = Number(val);
                }
                else {
                    params[key] = val;
                }
            });
        }
        return {
            handler,
            params: Object.keys(params).length > 0 ? params : undefined,
        };
    }
    /**
     * Extract MiniGameConfigData from a PGN string.
     * Returns null if no MiniGame header found.
     */
    static extractConfigData(pgn) {
        const miniGameHeader = PgnParser.getHeaderValue(pgn, "MiniGame");
        if (!miniGameHeader) {
            return null;
        }
        return this.parseMiniGameHeader(miniGameHeader);
    }
    /**
     * Build a full MiniGameConfig from a PGN string.
     * Combines the MiniGame header DSL with FEN and other headers.
     * Returns null if no MiniGame header found.
     */
    static extractConfig(pgn) {
        const configData = this.extractConfigData(pgn);
        if (!configData)
            return null;
        const fen = PgnParser.getHeaderValue(pgn, "FEN") || CHESS_EMPTY_BOARD_FEN;
        const playerColor = fen.includes(" b ") ? "b" : "w";
        const title = PgnParser.getHeaderValue(pgn, "Event") || undefined;
        // Build targets array from params if target/targets specified
        let targets;
        if (configData.params?.targets) {
            // Support comma-separated list: "e4,d5,f6"
            const targetsStr = String(configData.params.targets);
            targets = targetsStr
                .split(",")
                .filter((s) => isValidSquare(s))
                .map((s) => s.trim());
        }
        else if (configData.params?.target) {
            // Single target: "e4"
            const target = String(configData.params.target);
            if (isValidSquare(target)) {
                targets = [target];
            }
        }
        const config = {
            handler: configData.handler,
            fen,
            playerColor,
            params: configData.params,
            title,
            targets,
            moveLimit: configData.params?.moveLimit,
            hint: configData.params?.hint,
        };
        return config;
    }
    /**
     * Serialize a MiniGameConfigData to DSL header value.
     */
    static toDsl(configData) {
        if (!configData.params || Object.keys(configData.params).length === 0) {
            return configData.handler;
        }
        const paramPairs = Object.entries(configData.params)
            .map(([key, val]) => `${key}=${val}`)
            .join(";");
        return `${configData.handler}:${paramPairs}`;
    }
    /**
     * Generate a mini-game PGN from config.
     * Useful for exporting scenarios.
     */
    static toPgn(config) {
        let pgn = "";
        // Add Event header
        if (config.title) {
            pgn += `[Event "${config.title}"]\n`;
        }
        // Add MiniGame header with DSL
        const dsl = this.toDsl({ handler: config.handler, params: config.params });
        pgn += `[MiniGame "${dsl}"]\n`;
        // Add FEN headers
        pgn += '[SetUp "1"]\n';
        pgn += `[FEN "${config.fen}"]\n`;
        // No moves for mini-games, just the result marker
        pgn += "\n*\n";
        return pgn;
    }
}
